import {Component, computed, inject, model, OnInit, signal, input} from '@angular/core';
import {CommonModule} from '@angular/common';
import {
  ChapterResponse,
  ChapterControllerService,
  CompanyOfficeDetailResponse,
  ModuleFunctionControllerService,
  ModuleFunctionReference,
  UpdateProjectOfficeFunctionsRequest,
  ProgressStatus,
  ProjectRoleType,
  ProjectChapterType,
  ProjectControllerService,
  ProjectDetailResponse,
  ProjectOfficeResponse,
  ProjectUserControllerService,
  ProjectStepType,
} from '@frontend/shared-api';
import {TableModule} from 'primeng/table';
import {TranslocoDirective} from '@jsverse/transloco';
import {Accordion, AccordionContent, AccordionHeader, AccordionPanel} from 'primeng/accordion';
import {OfficeListService} from '../../../../../../shared/components/office-list/office-list.service';
import {CountryService} from '@frontend/shared';
import {Button} from 'primeng/button';
import {Module, ModuleFunction, DirectusService} from '@frontend/directus';
import {lastValueFrom} from 'rxjs';
import {FunctionSelectionDialogComponent} from '../../../../../../shared/components/dialog/function-selection-dialog/function-selection-dialog.component';
import {RoleCheckService} from '../../../../../../shared/services/role-check.service';
import {ToastService} from '@frontend/toast';
import {UserStateService} from '../../../../../../shared/services/user-state.service';
import {NoDataComponent} from '../../../../../../shared/components/no-data/no-data.component';
import {ConfirmationDialogService} from '../../../../../../shared/components/dialog/confirmation/confirmation-dialog.service';

interface ProjectOfficeRow extends CompanyOfficeDetailResponse {
  selectedFunctionIds: Set<string>;
  selectedFunctions: ModuleFunction[];
}

interface ContractFunctionGroup {
  moduleId: number;
  moduleName: string;
  functions: {
    functionId: number;
    functionName: string;
    selectedOfficeCount: number;
  }[];
}

@Component({
  selector: 'app-project-detail-company-list',
  imports: [
    CommonModule,
    TableModule,
    Button,
    TranslocoDirective,
    Accordion,
    AccordionPanel,
    AccordionHeader,
    AccordionContent,
    FunctionSelectionDialogComponent,
    NoDataComponent,
  ],
  templateUrl: './project-detail-company-list.component.html',
  styleUrl: './project-detail-company-list.component.scss',
})
export class ProjectDetailCompanyListComponent implements OnInit {
  readonly projectId = input<number>();
  readonly project = model<ProjectDetailResponse>();

  protected readonly offices = signal<ProjectOfficeRow[]>([]);
  protected readonly loading = signal(false);
  protected readonly dialogVisible = signal(false);
  protected readonly dialogOfficeName = signal('');
  protected readonly selectedOfficeId = signal<number | undefined>(undefined);
  protected readonly dialogInitialSelectedFunctions = signal<ModuleFunction[]>([]);
  protected readonly dialogReadOnly = signal(false);
  protected readonly isStep42NotStarted = signal(false);
  protected readonly dialogTranslationPrefix = 'wizard.project.dialog';
  private readonly hasProjectSetupAccess = signal(false);

  private readonly modulesById = signal<Map<number, Module>>(new Map());
  private readonly contractFunctionDirectusIds = signal<Set<string>>(new Set());
  private readonly allDirectusFunctionsById = signal<Map<string, ModuleFunction>>(new Map());

  protected readonly countryService = inject(CountryService);
  protected readonly canManageFunctions = computed(() => this.hasProjectSetupAccess());
  protected readonly canEditFunctions = computed(
    () => this.canManageFunctions() && this.isStep42NotStarted(),
  );
  protected readonly selectedFunctionOfficeCount = computed(() => {
    const counts = new Map<string, number>();
    this.offices().forEach((office) => {
      office.selectedFunctionIds.forEach((id) => {
        counts.set(id, (counts.get(id) || 0) + 1);
      });
    });
    return counts;
  });
  protected readonly contractFunctionGroups = computed<ContractFunctionGroup[]>(() => {
    const selectedCount = this.selectedFunctionOfficeCount();
    const availableIds = this.contractFunctionDirectusIds();

    return Array.from(this.modulesById().values())
      .map((module) => ({
        moduleId: module.id,
        moduleName: module.name,
        functions: (module.functions || [])
          .filter((fn) => availableIds.has(String(fn.id)))
          .map((fn) => ({
            functionId: fn.id,
            functionName: fn.name,
            selectedOfficeCount: selectedCount.get(String(fn.id)) || 0,
          })),
      }))
      .filter((group) => group.functions.length > 0);
  });

  private readonly companyListService = inject(OfficeListService);
  private readonly directusService = inject(DirectusService);
  private readonly moduleFunctionControllerService = inject(ModuleFunctionControllerService);
  private readonly projectControllerService = inject(ProjectControllerService);
  private readonly chapterControllerService = inject(ChapterControllerService);
  private readonly roleCheckService = inject(RoleCheckService);
  private readonly confirmDialogService = inject(ConfirmationDialogService);
  private readonly toastService = inject(ToastService);
  private readonly projectUserControllerService = inject(ProjectUserControllerService);
  private readonly userStateService = inject(UserStateService);

  ngOnInit(): void {
    if (this.roleCheckService.hasCustomerAdminRole()) {
      this.hasProjectSetupAccess.set(true);
    }
    void this.initializeData();
  }

  async selectFunctions(office: ProjectOfficeRow): Promise<void> {
    const latestStep42NotStarted = await this.refreshStep42Status();

    this.selectedOfficeId.set(office.officeId);
    this.dialogOfficeName.set(office.officeName);
    this.dialogInitialSelectedFunctions.set([...office.selectedFunctions]);
    this.dialogReadOnly.set(!(this.canManageFunctions() && latestStep42NotStarted));
    this.dialogVisible.set(true);
  }

  closeDialog(): void {
    this.dialogVisible.set(false);
    this.selectedOfficeId.set(undefined);
    this.dialogOfficeName.set('');
    this.dialogInitialSelectedFunctions.set([]);
    this.dialogReadOnly.set(false);
  }

  async onFunctionsSelected(functions: ModuleFunction[]): Promise<void> {
    const officeId = this.selectedOfficeId();
    if (!officeId || !this.projectId() || !this.project()) {
      this.closeDialog();
      return;
    }

    const updatedOffices = this.offices().map((office) =>
      office.officeId === officeId
        ? {
            ...office,
            selectedFunctions: [...functions],
            selectedFunctionIds: new Set(functions.map((fn) => String(fn.id))),
          }
        : office,
    );

    const shouldProceed =
      await this.confirmProceedWithMissingContractFunctionsIfNeeded(updatedOffices);
    if (!shouldProceed) {
      this.closeDialog();
      return;
    }

    await this.persistOfficeFunctions(updatedOffices);
    this.closeDialog();
  }

  private async initializeData(): Promise<void> {
    const projectId = this.projectId();
    if (!projectId || !this.project()) {
      return;
    }

    this.loading.set(true);
    try {
      const [offices, moduleMap, contractFunctions, chapters] = await Promise.all([
        lastValueFrom(this.companyListService.getAllOfficesWithDetails()),
        lastValueFrom(this.directusService.loadModuleFunctionsGroupedByModule()),
        lastValueFrom(this.moduleFunctionControllerService.getAllModuleFunctions()),
        lastValueFrom(this.chapterControllerService.getChaptersForProject({projectId: projectId})),
      ]);

      this.modulesById.set(moduleMap);
      this.contractFunctionDirectusIds.set(new Set(contractFunctions.map((fn) => fn.directusId)));
      this.allDirectusFunctionsById.set(this.createFunctionMap(moduleMap));
      this.isStep42NotStarted.set(this.resolveStep42NotStarted(chapters));
      this.hasProjectSetupAccess.set(await this.resolveProjectSetupAccess());
      this.offices.set(this.buildProjectOfficeRows(offices, this.project()!.offices));
    } finally {
      this.loading.set(false);
    }
  }

  private async resolveProjectSetupAccess(): Promise<boolean> {
    if (this.roleCheckService.hasCustomerAdminRole()) {
      return true;
    }

    const currentUser = this.userStateService.currentUser();
    const projectId = this.projectId();
    if (!currentUser || !projectId) {
      return false;
    }

    try {
      const projectUserDetail = await lastValueFrom(
        this.projectUserControllerService.getProjectUserWithDetails({
          projectId: projectId,
          userId: currentUser.id,
        }),
      );
      const roles = projectUserDetail.roles ? Array.from(projectUserDetail.roles) : [];
      return roles.includes(ProjectRoleType.PROJECT_MANAGER);
    } catch {
      return false;
    }
  }

  private createFunctionMap(moduleMap: Map<number, Module>): Map<string, ModuleFunction> {
    const functionMap = new Map<string, ModuleFunction>();
    Array.from(moduleMap.values()).forEach((module) => {
      (module.functions || []).forEach((moduleFunction) => {
        functionMap.set(String(moduleFunction.id), moduleFunction);
      });
    });
    return functionMap;
  }

  private buildProjectOfficeRows(
    allOffices: CompanyOfficeDetailResponse[],
    projectOffices: Set<ProjectOfficeResponse>,
  ): ProjectOfficeRow[] {
    const officeById = new Map(allOffices.map((office) => [office.officeId, office]));

    const rows = Array.from(projectOffices).map((projectOffice) => {
      const officeData = officeById.get(projectOffice.officeId);
      const selectedFunctionIds = new Set(
        Array.from(projectOffice.functions || []).map((fn: ModuleFunctionReference) =>
          String(fn.directusId),
        ),
      );

      const selectedFunctions = Array.from(selectedFunctionIds)
        .map((id) => this.allDirectusFunctionsById().get(id))
        .filter((fn): fn is ModuleFunction => !!fn);

      return {
        officeId: projectOffice.officeId,
        officeName: projectOffice.officeName,
        cityName: projectOffice.cityName,
        countryCode: projectOffice.countryCode,
        countryId: officeData?.countryId || 0,
        companyId: officeData?.companyId || 0,
        companyName: officeData?.companyName || '-',
        cityId: officeData?.cityId || 0,
        officeAddress: officeData?.officeAddress || '',
        projects: officeData?.projects || [],
        selectedFunctionIds,
        selectedFunctions,
      };
    });

    return rows.sort((a, b) => {
      const cmpCompany = a.companyName.localeCompare(b.companyName);
      if (cmpCompany !== 0) return cmpCompany;
      return a.officeName.localeCompare(b.officeName);
    });
  }

  private resolveStep42NotStarted(chapters: ChapterResponse[]): boolean {
    const chapter = chapters.find((item) => item.title === ProjectChapterType.EVALUATION_CRITERIA);
    const step = chapter?.steps?.find(
      (item) => item.title === ProjectStepType.EVALUATION_SUB_CRITERIA,
    );
    return step?.progress === ProgressStatus.NOT_STARTED;
  }

  private async refreshStep42Status(): Promise<boolean> {
    const projectId = this.projectId();
    if (!projectId) {
      return this.isStep42NotStarted();
    }

    try {
      const chapters = await lastValueFrom(
        this.chapterControllerService.getChaptersForProject({projectId: projectId}),
      );
      const latestStatus = this.resolveStep42NotStarted(chapters);
      this.isStep42NotStarted.set(latestStatus);
      return latestStatus;
    } catch {
      return this.isStep42NotStarted();
    }
  }

  private async persistOfficeFunctions(updatedOffices: ProjectOfficeRow[]): Promise<void> {
    const projectId = this.projectId();
    if (!projectId || !this.project()) {
      return;
    }

    const officesPayload = updatedOffices
      .map((office) => ({
        officeId: office.officeId,
        moduleFunctions: Array.from(office.selectedFunctionIds),
      }))
      .filter((office) => office.moduleFunctions.length > 0);

    try {
      const updatedProject = await lastValueFrom(
        this.projectControllerService.updateProjectOfficeFunctions(
          {
            id: projectId,
            updateProjectOfficeFunctionsRequest: {
              offices: officesPayload,
            } as unknown as UpdateProjectOfficeFunctionsRequest,
          },
          'body',
        ),
      );
      this.project.set(updatedProject);
      this.toastService.showSuccess('common.success', 'project.steps.sectionSaved');
      await this.initializeData();
    } catch {
      this.toastService.showError('common.error', 'error.generic.detail');
    }
  }

  private async confirmProceedWithMissingContractFunctionsIfNeeded(
    offices: ProjectOfficeRow[],
  ): Promise<boolean> {
    const selectedFunctionIds = new Set(
      offices.flatMap((office) => Array.from(office.selectedFunctionIds)),
    );
    const hasAllContractFunctionsSelected = Array.from(this.contractFunctionDirectusIds()).every(
      (id) => selectedFunctionIds.has(id),
    );

    if (hasAllContractFunctionsSelected) {
      return true;
    }

    return new Promise<boolean>((resolve) => {
      this.confirmDialogService.confirm({
        headerKey: 'project.functions.incompleteSelectionHeader',
        messageKey: 'project.functions.incompleteSelectionMessage',
        accept: () => resolve(true),
        reject: () => resolve(false),
      });
    });
  }
}
